import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { PageEvent } from '@angular/material/paginator';
import { MatOption, MatSelect } from '@angular/material/select';
import { Sort } from '@angular/material/sort';
import { catchError, filter, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { AdminPanel } from '../../core/interfaces';
import { TSP } from '../../core/models';
import { CompetenceService } from '../../core/services/competence/competence.service';
import { ConfirmationDialog } from '../../shared/components/confirmation-dialog/confirmation-dialog';
import { EntityListComponent } from '../../shared/components/entity-list/entity-list.component';
import { CompetenceForm, CompetenceFormData, CompetenceFormResult } from './competence-form/competence-form';
import {
  CompetenceMergeDialog,
  CompetenceMergeDialogData,
  CompetenceMergeDialogResult,
} from './competence-merge-dialog/competence-merge-dialog';

const { Kind, Status } = TSP.Internal.Dictionary.Competence;

export const COMPETENCE_KIND_LABELS: Record<TSP.Internal.Dictionary.Competence.Kind, string> = {
  [Kind.HARD]: 'Хард-скіл',
  [Kind.SOFT]: 'Софт-скіл',
  [Kind.SPECIALITY]: 'Спеціальність',
  [Kind.OTHER]: 'Інше',
};

export const COMPETENCE_STATUS_LABELS: Record<TSP.Internal.Dictionary.Competence.Status, string> = {
  [Status.PENDING]: 'Очікує',
  [Status.ACTIVE]: 'Активна',
  [Status.MERGED]: 'Злита',
};

interface CompetencesQueryState {
  pageIndex: number;
  pageSize: number;
  search: string;
  status: TSP.Internal.Dictionary.Competence.Status[];
  kind: TSP.Internal.Dictionary.Competence.Kind[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  candidatesMode: boolean;
  /** Bumped after every mutation so the grid reloads with the same filters. */
  refreshedAt: number;
}

/**
 * Taxonomy management: the grid over every competence plus the merge-review
 * bucket behind the «Кандидати на злиття» toggle. The bucket is a small,
 * unpaginated list served whole, so in that mode search filters client-side
 * and the paginator works over the loaded rows.
 *
 * All grid state lives in signals; `toObservable` on the computed query feeds
 * the HTTP pipeline, so any filter change triggers exactly one request per
 * stabilization.
 */
@Component({
  selector: 'app-competences',
  imports: [
    CommonModule,
    EntityListComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatCheckboxModule,
  ],
  templateUrl: './competences.component.html',
  styleUrl: './competences.component.scss',
})
export class CompetencesComponent {
  private readonly competenceService = inject(CompetenceService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  public readonly kindOptions = Object.values(Kind).map((kind) => ({
    value: kind,
    label: COMPETENCE_KIND_LABELS[kind],
  }));
  public readonly statusOptions = Object.values(Status).map((status) => ({
    value: status,
    label: COMPETENCE_STATUS_LABELS[status],
  }));

  public readonly gridColumns: AdminPanel.Entity.EntityColumn[] = [
    { key: 'id', label: 'ID', sortable: true, sortKey: 'id', type: 'number' },
    { key: 'name', label: 'Назва', sortable: true, sortKey: 'label', type: 'text' },
    { key: 'enName', label: 'English', sortable: false, type: 'text' },
    { key: 'ruName', label: 'Русский', sortable: false, type: 'text' },
    { key: 'ukName', label: 'Українська', sortable: false, type: 'text' },
    { key: 'kind', label: 'Тип', sortable: true, sortKey: 'kind', type: 'enum', options: COMPETENCE_KIND_LABELS },
    {
      key: 'status',
      label: 'Статус',
      sortable: true,
      sortKey: 'status',
      type: 'enum',
      options: COMPETENCE_STATUS_LABELS,
    },
    {
      key: 'profileCount',
      label: 'Кандидати',
      sortable: true,
      sortKey: 'profileCount',
      type: 'count-icon',
      iconWhenEmpty: 'person_off',
      iconWhenFilled: 'person',
    },
  ];

  public readonly candidateColumns: AdminPanel.Entity.EntityColumn[] = [
    { key: 'id', label: 'ID', sortable: false, type: 'number' },
    { key: 'name', label: 'Назва', sortable: false, type: 'text' },
    { key: 'kind', label: 'Тип', sortable: false, type: 'enum', options: COMPETENCE_KIND_LABELS },
    { key: 'mergedIntoName', label: 'Пропоноване злиття', sortable: false, type: 'text' },
  ];

  public readonly candidatesMode = signal(false);
  public readonly pageIndex = signal(0);
  public readonly pageSize = signal(50);
  public readonly loading = signal(false);
  /** Set when the last load failed; the stream itself stays alive and retries on the next change. */
  public readonly loadFailed = signal(false);

  public readonly columns = computed(() => (this.candidatesMode() ? this.candidateColumns : this.gridColumns));

  private readonly search = signal('');
  private readonly status = signal<TSP.Internal.Dictionary.Competence.Status[]>([]);
  private readonly kind = signal<TSP.Internal.Dictionary.Competence.Kind[]>([]);
  private readonly sortBy = signal('label');
  private readonly sortOrder = signal<'asc' | 'desc'>('asc');
  private readonly refreshedAt = signal(0);

  private readonly queryState = computed<CompetencesQueryState>(() => ({
    pageIndex: this.pageIndex(),
    pageSize: this.pageSize(),
    search: this.search(),
    status: this.status(),
    kind: this.kind(),
    sortBy: this.sortBy(),
    sortOrder: this.sortOrder(),
    candidatesMode: this.candidatesMode(),
    refreshedAt: this.refreshedAt(),
  }));

  private readonly data$ = toObservable(this.queryState).pipe(
    tap(() => this.loading.set(true)),
    // Recover INSIDE the switchMap: an error escaping to the outer stream
    // would terminate the shared subscription, leaving every later
    // search/filter/page change dead until a full reload.
    switchMap((state) =>
      this.loadPage(state).pipe(
        tap(() => this.loadFailed.set(false)),
        catchError(() => {
          this.loadFailed.set(true);
          return of({ items: [], totalCount: 0 });
        }),
      ),
    ),
    tap(() => this.loading.set(false)),
    shareReplay(1),
  );

  public readonly competences$ = this.data$.pipe(
    map((response) => response.items as TSP.Internal.Dictionary.Competence.AdminItem[]),
  );
  public readonly totalItems$: Observable<number> = this.data$.pipe(map((response) => response.totalCount));

  /** Bucket mode: confirm / reject the suggestion. Grid mode: promote a PENDING row. */
  public readonly customRowActions = (item: AdminPanel.Entity.EntityType): AdminPanel.Entity.EntityRowAction[] => {
    const competence = item as TSP.Internal.Dictionary.Competence.AdminItem;

    if (this.candidatesMode()) {
      return [
        {
          type: 'merge',
          icon: 'merge',
          tooltip: `Підтвердити злиття у «${competence.mergedIntoName}»`,
          color: 'accent',
        },
        { type: 'reject-merge', icon: 'block', tooltip: 'Відхилити пропозицію', color: 'warn' },
      ];
    }

    if (competence.status === Status.PENDING) {
      return [{ type: 'promote', icon: 'check_circle', tooltip: 'Підтвердити (зробити активною)', color: 'primary' }];
    }

    return [];
  };

  /** Fold this competence into a hand-picked target. Pointless on an already merged row. */
  public readonly endRowActions = (item: AdminPanel.Entity.EntityType): AdminPanel.Entity.EntityRowAction[] => {
    const competence = item as TSP.Internal.Dictionary.Competence.AdminItem;
    if (this.candidatesMode() || competence.status === Status.MERGED) {
      return [];
    }
    return [{ type: 'merge', icon: 'swap_horiz', tooltip: 'Злити в іншу компетенцію', color: 'accent' }];
  };

  /** Deleting a used or merged row would orphan links — those go through merge instead. */
  public readonly showDelete = (item: AdminPanel.Entity.EntityType): boolean => {
    const competence = item as TSP.Internal.Dictionary.Competence.AdminItem;
    return !this.candidatesMode() && competence.status !== Status.MERGED && competence.profileCount === 0;
  };

  public readonly rowClass = (item: AdminPanel.Entity.EntityType): string => {
    const competence = item as TSP.Internal.Dictionary.Competence.AdminItem;
    return competence.status === Status.MERGED ? 'row--muted' : '';
  };

  public onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  public onSortChange(sort: Sort): void {
    this.pageIndex.set(0);
    this.sortBy.set(sort.active);
    this.sortOrder.set((sort.direction || 'asc') as 'asc' | 'desc');
  }

  public onSearch(searchTerm: string): void {
    this.pageIndex.set(0);
    this.search.set(searchTerm);
  }

  public onStatusFilter(status: TSP.Internal.Dictionary.Competence.Status[]): void {
    this.pageIndex.set(0);
    this.status.set(status ?? []);
  }

  public onKindFilter(kind: TSP.Internal.Dictionary.Competence.Kind[]): void {
    this.pageIndex.set(0);
    this.kind.set(kind ?? []);
  }

  public onCandidatesModeChange(checked: boolean): void {
    this.pageIndex.set(0);
    this.candidatesMode.set(checked);
  }

  public onAction(action: AdminPanel.Entity.EntityAction): void {
    const item = action.item as TSP.Internal.Dictionary.Competence.AdminItem | undefined;

    switch (action.type) {
      case 'create':
        this.openCompetenceForm(undefined);
        break;
      case 'edit':
        if (item) {
          this.openCompetenceForm(item);
        }
        break;
      case 'delete':
        if (item) {
          this.deleteCompetence(item);
        }
        break;
      case 'promote':
        if (item) {
          this.promoteCompetence(item);
        }
        break;
      case 'merge':
        if (item && this.candidatesMode()) {
          this.confirmSuggestedMerge(item);
        } else if (item) {
          this.openMergeDialog(item);
        }
        break;
      case 'reject-merge':
        if (item) {
          this.rejectSuggestedMerge(item);
        }
        break;
    }
  }

  private loadPage(
    state: CompetencesQueryState,
  ): Observable<TSP.Helpers.EntitiesList<TSP.Internal.Dictionary.Competence.MergeCandidate>> {
    if (state.candidatesMode) {
      return this.loadMergeCandidates(state);
    }

    return this.competenceService.getCompetencesList({
      page: state.pageIndex + 1,
      size: state.pageSize,
      search: state.search || undefined,
      status: state.status.length ? state.status : undefined,
      kind: state.kind.length ? state.kind : undefined,
      sortBy: state.sortBy as TSP.Internal.Dictionary.Competence.AdminQuery['sortBy'],
      sortOrder: state.sortOrder.toUpperCase() as 'ASC' | 'DESC',
    });
  }

  /**
   * The bucket endpoint returns the whole list, so paging and search happen
   * here; the shape is normalized to EntitiesList to share the pipeline.
   */
  private loadMergeCandidates(
    state: CompetencesQueryState,
  ): Observable<TSP.Helpers.EntitiesList<TSP.Internal.Dictionary.Competence.MergeCandidate>> {
    return this.competenceService.getMergeCandidates().pipe(
      map((candidates) => {
        const term = state.search.trim().toLowerCase();
        const filtered = term
          ? candidates.filter(
              (candidate) =>
                candidate.name.toLowerCase().includes(term) ||
                (candidate.mergedIntoName ?? '').toLowerCase().includes(term),
            )
          : candidates;

        return {
          items: filtered.slice(state.pageIndex * state.pageSize, (state.pageIndex + 1) * state.pageSize),
          totalCount: filtered.length,
        };
      }),
    );
  }

  private openCompetenceForm(competence: TSP.Internal.Dictionary.Competence.AdminItem | undefined): void {
    this.dialog
      .open(CompetenceForm, {
        maxWidth: '760px',
        width: '720px',
        data: { competence } satisfies CompetenceFormData,
      })
      .afterClosed()
      .pipe(
        filter((result): result is CompetenceFormResult => !!result),
        switchMap((result) =>
          result.type === 'create'
            ? this.competenceService.createCompetence(result.payload)
            : this.competenceService.updateCompetence(result.id, result.payload),
        ),
        tap(() => this.refreshedAt.set(Date.now())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private deleteCompetence(competence: TSP.Internal.Dictionary.Competence.AdminItem): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: { text: `Видалити компетенцію "${competence.name}"? Цю дію неможливо скасувати.` },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.competenceService.deleteCompetence(competence.id)),
        tap(() => this.refreshedAt.set(Date.now())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private promoteCompetence(competence: TSP.Internal.Dictionary.Competence.AdminItem): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: { text: `Підтвердити компетенцію "${competence.name}"? Вона з'явиться у словнику для рекрутерів.` },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.competenceService.updateCompetence(competence.id, {
            label: competence.name,
            kind: competence.kind,
            status: Status.ACTIVE,
          }),
        ),
        tap(() => this.refreshedAt.set(Date.now())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private openMergeDialog(competence: TSP.Internal.Dictionary.Competence.AdminItem): void {
    this.dialog
      .open(CompetenceMergeDialog, {
        maxWidth: '560px',
        width: '520px',
        data: { competence } satisfies CompetenceMergeDialogData,
      })
      .afterClosed()
      .pipe(
        filter((result): result is CompetenceMergeDialogResult => !!result),
        switchMap((result) =>
          this.competenceService.mergeCompetences({ sourceId: competence.id, targetId: result.targetId }),
        ),
        tap(() => this.refreshedAt.set(Date.now())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private confirmSuggestedMerge(candidate: TSP.Internal.Dictionary.Competence.AdminItem): void {
    if (candidate.mergedIntoId === null) {
      return;
    }

    this.dialog
      .open(ConfirmationDialog, {
        data: {
          text: `Злити "${candidate.name}" у "${candidate.mergedIntoName}"? Цю дію неможливо скасувати.`,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.competenceService.mergeCompetences({
            sourceId: candidate.id,
            // Narrowed by the early return above; the pointer is the suggested target.
            targetId: candidate.mergedIntoId as number,
          }),
        ),
        tap(() => this.refreshedAt.set(Date.now())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private rejectSuggestedMerge(candidate: TSP.Internal.Dictionary.Competence.AdminItem): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          text: `Відхилити пропозицію злиття "${candidate.name}" → "${candidate.mergedIntoName}"? Пара більше не пропонуватиметься.`,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.competenceService.rejectMerge({ sourceId: candidate.id })),
        tap(() => this.refreshedAt.set(Date.now())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
